import { useCallback, useEffect, useMemo, useState } from 'react'
import { saveBlobAsFile } from '../../../../api/apiUtils.js'
import {
  deleteVendorLDC,
  downloadVendorLDCData,
  searchFilterVendorLDC,
  updateVendorLDC,
} from '../../../../api/vendorMasterApi.js'
import { VENDOR_LDC_DETAILS_COLUMNS, vendorLdcDetailsSchema, buildMonthlyColumns } from '../../../../constants/define.jsx'
import { getValidCriteria, DEFAULT_PAGE } from '../../../../constants/index.js'
import { useSnackbar } from '../../../../contexts/SnackbarProvider.jsx'
import { CommonTable } from '../../../common/index.js'

const VendorLdcDetails = ({
  isEditMode,
  searchCOList,
  tanUuid,
  downloadExcel,
  searchAction,
  onActiveTabData,
  deleteData,
  isCloseEditMode,
  editingData,
  onFieldChange,
  triggerSave,
  visibleColumns,
  setTriggerSave,
  setEditingData,
  setIsEditMode,
  setDownloadExcel,
  setDisabled,
  setConfirmConfig,
  setConfirmOpen,
  pendingDelete,
  confirmOpen,
  setDeleteData,
}) => {
  // State
  const [tableData, setTableData] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [isMonthlyView, setIsMonthlyView] = useState(false)
  const filteredColumns = useMemo(() => {
    const baseColumns = VENDOR_LDC_DETAILS_COLUMNS.filter(col =>
      visibleColumns.includes(col.id)
    )
    if (!isMonthlyView) return baseColumns

    // Replace 'amountUtilised' column with grouped monthly columns
    return baseColumns.map(col => {
      if (col.id === 'amountUtilised') {
        const grouped = buildMonthlyColumns(tableData)
        // If no monthly data found, keep original column
        if (!grouped.columns || grouped.columns.length === 0) return col
        return grouped
      }
      return col
    })
  }, [visibleColumns, isMonthlyView, tableData])


  // Handlers
  const { showSnackbar } = useSnackbar()

  const createPayload = (currentPage, pageSize, searchCOList) => {
    return {
      pageFilter: {
        currentPage: currentPage,
        pageSize: pageSize,
        sortField: 'vendorName',
        sortOrder: 'ASC',
        includeCount: true,
      },
      userRoleType: 'ADMIN',
      searchCOList: getValidCriteria(searchCOList),
    }
  }

  const stablePayload = useMemo(
    () => createPayload(currentPage, itemsPerPage, searchCOList),
    [currentPage, itemsPerPage, searchCOList]
  )

  const fetchVendorLDCTableData = useCallback(async () => {
    if (!tanUuid) return
    setLoading(true)
    const result = await searchFilterVendorLDC(stablePayload, tanUuid)
    if (result?.success) {
      setTableData(result.data.content)
      setTotalItems(result.data.totalElements)
      setLoading(false)
    } else if (!result?.success && result?.error) {
      setTableData([])
      setLoading(false)
      showSnackbar(result.error, 'error')
    }
  }, [stablePayload, tanUuid])

  const deleteVendorMasterData = useCallback(async () => {
    const payload = selectedRows.map(uuid => ({ uuid }))
    const selectedRowData = tableData.filter(r => selectedRows.includes(r.uuid))
    const hasAmountUtilised = selectedRowData.some(
      row => row.amountUtilised !== null && Number(row.amountUtilised) > 0
    )

    const result = await deleteVendorLDC(payload)

    if (result?.success && result?.data?.success) {
      showSnackbar(hasAmountUtilised ? 'The threshold has been updated based on your delete action. Please re‑run the Rate Suggestion on the Processed tab' : 'Vendor deleted successfully', 'success')
      await fetchVendorLDCTableData()
      setSelectedRows([])
    } else {
      const errorMessage = result?.data?.errors?.[0]?.message || 'Error while deleting vendor ldc details'
      showSnackbar(errorMessage, 'error')
    }
  }, [selectedRows, fetchVendorLDCTableData])

  const downloadVendorLDCExcel = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    const payload = {
      pageFilter: {
        currentPage,
        pageSize: DEFAULT_PAGE,
        sortField: 'vendorName',
        sortOrder: 'ASC',
        includeCount: true,
      },
      userRoleType: 'ADMIN',
      searchCOList: getValidCriteria(searchCOList),
      isToggle: isMonthlyView,
    }
    const result = await downloadVendorLDCData(payload, tanUuid)
    if (result?.success && result?.data) {
      saveBlobAsFile(result.data, 'VendorLDC.xlsx')
      if (isMonthlyView) {
        showSnackbar('Monthly view Excel downloaded. This file cannot be reloaded in the system.', 'info')
      }
    } else if (result?.error) {
      showSnackbar(result.error, 'error')
    }
  }

  const handleSelectRow = uuid => {
    setSelectedRows(prev =>
      prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
    )
    setDownloadExcel(0)
    setDeleteData(0)
  }

  const handleSelectAll = e => {
    setSelectedRows(e.target.checked ? tableData.map(r => r.uuid) : [])
    setDownloadExcel(0)
    setDeleteData(0)
  }
  useEffect(() => {
    if (isCloseEditMode) setSelectedRows([])
  }, [isCloseEditMode])

  useEffect(() => {
    if (selectedRows.length == 0) {
      setDisabled(true)
      return
    } else {
      setDisabled(false)
    }
    if (deleteData) {
      deleteVendorMasterData()
    }
  }, [deleteData, deleteVendorMasterData])

  useEffect(() => {
    if (selectedRows.length === 0) {
      setDisabled(true)
      return
    } else {
      setDisabled(false)
    }

    if (confirmOpen) {
      const selectedRowData = tableData.filter(r =>
        selectedRows.includes(r.uuid)
      )
      const hasAmountUtilised = selectedRowData.some(
        row => row.amountUtilised !== null && Number(row.amountUtilised) > 0
      )

      if (hasAmountUtilised) {
        setConfirmConfig({
          title: 'Are you sure you want to delete selected record?',
          message:
            'On deletion of selected LDC certificate there is some utilised LDC limit which will require recomputation of TDS liability by clicking on "Suggest TDS Rate". Do you want to continue?',
          confirmText: 'Proceed',
        })
      } else {
        setConfirmConfig({
          title: 'Delete Confirmation',
          message: 'Are you sure you want to delete the selected record(s)?',
          confirmText: 'Proceed',
        })
      }
    }
  }, [deleteData, tableData, selectedRows, confirmOpen])

  const handlePageChange = page => {
    setCurrentPage(page)
    setDownloadExcel(0)
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    setDownloadExcel(0)
  }

  // Side Effects
  useEffect(() => {
    const controller = new AbortController()
    fetchVendorLDCTableData()
    return () => {
      controller.abort()
    }
  }, [fetchVendorLDCTableData])

  useEffect(() => {
    onActiveTabData({ type: 'editEnabled', value: selectedRows.length > 0 })
  }, [selectedRows, onActiveTabData])

  useEffect(() => {
    onActiveTabData({ type: 'monthlyToggle', value: isMonthlyView, handler: setIsMonthlyView })
  }, [isMonthlyView, onActiveTabData])

  useEffect(() => {
    if (!downloadExcel) return
    // Reset the trigger immediately so re-renders (from snackbar/state updates
    // inside the download) don't re-fire this effect and call the API again.
    setDownloadExcel(0)
    downloadVendorLDCExcel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadExcel])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchAction])

  useEffect(() => {
    if (!triggerSave) return

    const entries = Object.entries(editingData || {})
    if (!entries.length) {
      showSnackbar('No rows to save', 'error')
      setTriggerSave(0)
      return
    }

    const validRowsPayload = [] // payload items like { uuid, field1, field2 }
    const invalidMessages = [] // collect messages (one string per issue)

    entries.forEach(([uuid, changes], idx) => {
      const originalRow = tableData.find(r => r.uuid === uuid)

      if (!originalRow) {
        const msg = `Row for UUID ${uuid} not found on current page`
        invalidMessages.push(msg)
        return
      }

      const rowNumber =
        tableData.findIndex(r => r.uuid === uuid) >= 0
          ? tableData.findIndex(r => r.uuid === uuid) + 1
          : idx + 1

      // sanitize only edited fields
      const sanitizedChanges = { ...changes }
      if (
        sanitizedChanges.contactNumber !== undefined &&
        sanitizedChanges.contactNumber !== null
      ) {
        sanitizedChanges.contactNumber = String(
          sanitizedChanges.contactNumber
        ).replace(/\D/g, '')
      }

      // Validate merged row (ensures schema rules that depend on other fields still pass)
      const mergedRow = { ...originalRow, ...sanitizedChanges }
      const result = vendorLdcDetailsSchema.safeParse(mergedRow)

      if (result.success) {
        // prepare payload as uuid + only edited (sanitized) fields
        validRowsPayload.push({
          uuid,
          ...sanitizedChanges,
        })
      } else {
        const issues = result.error?.issues || []
        if (issues.length === 0) {
          invalidMessages.push(`Row ${rowNumber}: Validation failed`)
        } else {
          issues.forEach(issue => {
            const field = issue.path?.[0] ?? 'unknown'
            const msg = `Row ${rowNumber} [${field}]: ${issue.message}`
            invalidMessages.push(msg)
          })
        }
      }
    })

    if (invalidMessages.length > 0) {
      const delay = 700
      invalidMessages.forEach((m, i) => {
        setTimeout(() => showSnackbar(m, 'error'), i * delay)
      })

      setTriggerSave(0)
      return
    }

    if (validRowsPayload.length === 0) {
      showSnackbar('No valid changes to update', 'error')
      setTriggerSave(0)
      return
    }

    const saveAll = async () => {
      try {
        const result = await updateVendorLDC(validRowsPayload)

        if (result?.success && result?.data?.success) {
          showSnackbar('Vendors updated successfully', 'success')

          // remove only saved rows from editingData
          const updatedEditingData = { ...editingData }
          validRowsPayload.forEach(r => delete updatedEditingData[r.uuid])
          setEditingData(updatedEditingData)

          setSelectedRows([])
          setIsEditMode && setIsEditMode(false)
          await fetchVendorLDCTableData()
        } else {
          const errorMessage = result?.data?.errors?.[0]?.message || 'Error while updating vendor ldc details'
          showSnackbar(errorMessage, 'error')
        }
      } catch (err) {
        console.error("ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â¥ API call failed:", err)
        showSnackbar('Error updating vendors', 'error')
      } finally {
        setTriggerSave(0)
      }
    }

    saveAll()
  }, [
    triggerSave,
    tableData,
    fetchVendorLDCTableData,
    setEditingData,
    setTriggerSave,
    setSelectedRows,
    setIsEditMode,
  ])

  return (
    <CommonTable
      columns={filteredColumns}
      data={tableData}
      editMode={isEditMode}
      selectedItems={selectedRows}
      loading={loading}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      currentPage={currentPage}
      showActions={false}
      showCheckbox={true}
      showFooter={true}
      showItemsPerPage={true}
      showTotalItems={true}
      showPageNumbers={true}
      emptyMessage="No data found"
      onSelectItem={handleSelectRow}
      onSelectAll={handleSelectAll}
      onPageChange={handlePageChange}
      onItemsPerPageChange={handleItemsPerPageChange}
      editingData={editingData}
      onFieldChange={onFieldChange}
    />
  )
}

export default VendorLdcDetails
